import { useState, useEffect, createContext, useContext } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || '';

export interface ThresholdConfig {
  walkOn: number;
  short: number;
  moderate: number;
  long: number;
  note?: string | null;
}

// Module-level map that gets populated on first API fetch.
// Used as a fallback when no context is available.
let thresholdsMap: Record<string, ThresholdConfig> = {};

// Shared promise — prevents double-fetch when module preload and provider both activate.
let fetchPromise: Promise<void> | null = null;

interface ApiThresholdDto {
  walkOn: number;
  shortWait: number;
  moderateWait: number;
  longWait: number;
  note?: string | null;
}

function fetchThresholds(): Promise<void> {
  if (fetchPromise) return fetchPromise;
  fetchPromise = fetch(`${API_BASE}/api/admin/attraction-thresholds`)
    .then(res => {
      if (!res.ok) return {};
      return res.json();
    })
    .then((data: Record<string, ApiThresholdDto>) => {
      const map: Record<string, ThresholdConfig> = {};
      for (const [id, cfg] of Object.entries(data)) {
        map[id] = {
          walkOn: cfg.walkOn,
          short: cfg.shortWait,
          moderate: cfg.moderateWait,
          long: cfg.longWait,
          note: cfg.note,
        };
      }
      thresholdsMap = map;
    })
    .catch(() => {
      // If fetch fails, thresholdsMap stays empty (will use frontend defaults)
    });
  return fetchPromise;
}

// Preload thresholds on module load (non-blocking)
fetchThresholds();

export { thresholdsMap, fetchThresholds };

// ------ Context-based provider (for components that need it) ------

export interface ThresholdsContextValue {
  thresholds: Record<string, ThresholdConfig>;
  isLoading: boolean;
}

const ThresholdsContext = createContext<ThresholdsContextValue>({
  thresholds: {},
  isLoading: false,
});

export function ThresholdsProvider({ children }: { children: React.ReactNode }) {
  const [thresholds, setThresholds] = useState<Record<string, ThresholdConfig>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // Await the shared promise — if the module-level preload already started the fetch,
    // this waits on the same request instead of firing a second one.
    fetchThresholds().then(() => {
      if (cancelled) return;
      setThresholds(thresholdsMap);
      setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <ThresholdsContext.Provider value={{ thresholds, isLoading }}>
      {children}
    </ThresholdsContext.Provider>
  );
}

export function useThresholds() {
  return useContext(ThresholdsContext);
}
